import { Button, Grid, IconShoppingCartSimple } from '@vtex/admin-ui'
import { useFormState, useWatch } from '@vtex/admin-ui-form'
import { useSkusImport } from 'hooks'
import { useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { LIMITS } from 'shared'
import {
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  NumberInput,
  Rule,
  RuleContent,
  RuleStack,
  SkuSelector,
  UseDrawerStateReturn,
} from 'ui'
import { BuyTogetherBenefit, BuyTogetherTrigger } from '../../../../../typings'
import styles from '../styles'
import { messages } from './messages'
import { suite } from './validation'
const { vestResolver } = require('@hookform/resolvers/vest')

type BuyTogetherTriggerEditorProps = {
  state: UseDrawerStateReturn
  initialConfig: BuyTogetherTrigger | undefined
  onSave: (config: BuyTogetherTrigger) => void
  benefit?: BuyTogetherBenefit
}

export function BuyTogetherTriggerEditor(props: BuyTogetherTriggerEditorProps) {
  const { state, initialConfig, onSave, benefit } = props
  const intl = useIntl()
  const { $t } = intl

  const form = useFormState<BuyTogetherTrigger>({
    defaultValues: initialConfig,
    resolver: vestResolver(suite(intl, benefit)),
  })

  const initialNotNullable = useMemo(
    () =>
      !!initialConfig
        ? { ...initialConfig }
        : { skus: [], discount: 0, minQuantity: 1 },
    [initialConfig]
  )

  useEffect(() => {
    state.open && form.reset(initialNotNullable)
  }, [form, initialNotNullable, state.open])

  const skus = useWatch({
    form,
    name: 'skus',
  })

  const { importSkus, loading: importingSkus } = useSkusImport()

  const handleSave = async () => {
    const valid = await form.trigger()

    if (!valid) return

    onSave(form.getValues())

    state.hide()
  }

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>
          <FormattedMessage {...messages.title} values={{ index: 1 }} />
        </DrawerTitle>
        <DismissButton onHide={state.hide} />
      </DrawerHeader>
      <DrawerContent>
        <div className={styles.editor.trigger.content}>
          <RuleStack>
            <Rule>
              <RuleContent fluid>
                <SkuSelector>
                  <SkuSelector.Header>
                    <SkuSelector.Download values={skus} />
                    <SkuSelector.Upload
                      name="skus"
                      form={form}
                      importer={importSkus}
                      maxEntries={LIMITS.MAX_BUY_TOGETHER_SKUS}
                    />
                  </SkuSelector.Header>
                  <SkuSelector.Selector
                    name="skus"
                    state={form}
                    loading={importingSkus}
                    maxEntries={LIMITS.MAX_BUY_TOGETHER_SKUS}
                  />
                </SkuSelector>
              </RuleContent>
            </Rule>
          </RuleStack>

          <Grid
            templateColumns="1fr 1fr"
            className={styles.editor.trigger.inputGrid}
          >
            <NumberInput
              state={form}
              name={`minQuantity`}
              suffix={<IconShoppingCartSimple />}
              min={0}
              fractionDigits={0}
              label={$t(messages.minQuantity)}
            />
            <NumberInput
              state={form}
              name="discount"
              suffix="%"
              min={0}
              max={100}
              label={$t(messages.discount)}
            />
          </Grid>
        </div>
      </DrawerContent>

      <DrawerFooter>
        <Button variant="tertiary" onClick={state.hide}>
          <FormattedMessage {...messages.cancel} />
        </Button>
        <Button variant="primary" onClick={handleSave}>
          <FormattedMessage {...messages.apply} />
        </Button>
      </DrawerFooter>
    </Drawer>
  )
}
